import { useMemo } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../layout/Routes'

import { Flex, MountableTab, Tabs } from '@percent/lemonade'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { WaiverTemplate } from '@percent/partner-dashboard/services/templates/waiverTemplateService.types'
import { SurveyTemplate } from '@percent/partner-dashboard/services/templates/surveyTemplateService.types'
import { TemplatesList } from './TemplatesList/TemplatesList'

enum TemplateTabs {
  WAIVER = 'waiver',
  SURVEY = 'survey'
}

export function TemplatesContainer() {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const { surveyTemplateService, waiverTemplateService } = useServices()
  const [tabQP, setTabQP] = useQueryParam('tab', StringParam)

  const canViewSurvey = userCan('viewSurveyTemplates')
  const canViewWaiver = userCan('viewWaiverTemplates')

  useBreadcrumbs([{ label: t('breadcrumb.templates') }])

  const tabs = useMemo(
    () => [
      ...(canViewWaiver ? [{ label: t('typography.templates.waiverTab'), key: TemplateTabs.WAIVER }] : []),
      ...(canViewSurvey ? [{ label: t('typography.templates.surveyTab'), key: TemplateTabs.SURVEY }] : [])
    ],
    [canViewSurvey, canViewWaiver, t]
  )

  const activeTab = useMemo(() => {
    const fallback = tabs[0]?.key ?? TemplateTabs.WAIVER

    if (!tabQP) {
      return fallback
    }

    return tabs.find(tab => tab.key === tabQP)?.key ?? fallback
  }, [tabs, tabQP])

  return (
    <Flex direction="column" bg="white" style={{ borderRadius: '8px' }}>
      <Tabs items={tabs} activeTab={activeTab} onChangeTab={setTabQP} />
      {canViewWaiver && (
        <MountableTab index={TemplateTabs.WAIVER} value={activeTab}>
          <TemplatesList<WaiverTemplate>
            listFn={waiverTemplateService.getWaiverTemplatesList}
            archiveTemplate={waiverTemplateService.archiveWaiverTemplate}
            setDefaultTemplate={waiverTemplateService.setDefaultWaiverTemplate}
            removeDefaultTemplate={waiverTemplateService.removeDefaultWaiverTemplate}
            canManage={userCan('manageWaiverTemplates')}
            searchPlaceholder={t('typography.templates.waiverSearchPlaceholder')}
            createButtonText={t('button.newWaiverTemplate')}
            emptyTitle={t('typography.templates.waiverEmptyTitle')}
            emptyDescription={t('typography.templates.waiverEmptyDescription')}
            testIdPrefix="waiver-template"
            createRoute={RoutePath.WORKPLACE_GIVING_TEMPLATES_WAIVER_NEW}
            editRoute={RoutePath.WORKPLACE_GIVING_TEMPLATES_WAIVER_EDIT}
          />
        </MountableTab>
      )}
      {canViewSurvey && (
        <MountableTab index={TemplateTabs.SURVEY} value={activeTab}>
          <TemplatesList<SurveyTemplate>
            listFn={surveyTemplateService.getSurveyTemplatesList}
            archiveTemplate={surveyTemplateService.archiveSurveyTemplate}
            setDefaultTemplate={surveyTemplateService.setDefaultSurveyTemplate}
            removeDefaultTemplate={surveyTemplateService.removeDefaultSurveyTemplate}
            nameTooltip={template => t(`typography.templates.surveyType.${template.surveyType}`)}
            canManage={userCan('manageSurveyTemplates')}
            searchPlaceholder={t('typography.templates.surveySearchPlaceholder')}
            createButtonText={t('button.newSurveyTemplate')}
            emptyTitle={t('typography.templates.surveyEmptyTitle')}
            emptyDescription={t('typography.templates.surveyEmptyDescription')}
            testIdPrefix="survey-template"
            createRoute={RoutePath.WORKPLACE_GIVING_TEMPLATES_SURVEY_NEW}
            editRoute={RoutePath.WORKPLACE_GIVING_TEMPLATES_SURVEY_EDIT}
          />
        </MountableTab>
      )}
    </Flex>
  )
}
